// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useReportingMap } from 'sonata-platform-components';
import { Map as MapComponent } from 'sonata-platform-components/lib/types/types/reportingMap.types';
import { ButtonUI, Selector, SelectorOptionPropsInterface } from 'sonata-react-components';

import { reportingSetCartographyMapConfig } from '@/store/actions/reporting.actions';
import { reportingAdvancedFilterResetCartography } from '@/store/actions/reportingAdvancedFilter.actions';
import { getCountriesWithEvents } from '@/store/selectors/reporting.selectors';
import {
  getCartographyCountries,
  getCartographyLevel,
  getCartographyLevelIds
} from '@/store/selectors/reportingAdvancedFilter.selectors';
import { COUNTRY_LEVEL_CODE } from '@/ts-modules/reporting/Map/helpers';
import { useCartography } from '@/ts-modules/reporting/reporting.hooks';
import { getCartographyDictionary } from '@/ts-resources/reporting.resources';
import { ButtonsContainer, CartographyAdvanceSettingsStyle } from './cartographyAdvanceSettings.style';

type Props = {
  mapId: MapComponent['id'];
};

type State = {
  cartographyLevelOptions: Record<string, any>[];
  countriesOptionsSelected: string[];
  levelOptionSelected: string;
  loadingCartographyLevels: boolean;
};

export const CartographyAdvanceSettings: React.VFC<Props> = ({ mapId }) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const campaignCountries = useSelector(getCountriesWithEvents) || [];
  // @ts-ignore
  const reportingMapCountries = useSelector(store => getCartographyCountries(store, 'maps', mapId));
  // @ts-ignore
  const defaultCartographyCountries = useSelector(store => getCartographyCountries(store, 'general'));
  const generalCartographyLevel = useSelector(getCartographyLevel);
  const generalCartographyLevelIds = useSelector(getCartographyLevelIds);
  const reportingCountries = defaultCartographyCountries.length
    ? defaultCartographyCountries
    : reportingMapCountries.length
      ? reportingMapCountries
      : campaignCountries;

  const { getMap, resetMapCartographyFilter, resetMapCartographySettings, setMapCartographySettings } =
    useReportingMap();
  const map = getMap({ id: mapId });

  const {
    settings: {
      cartography,
      cartography: { countries, level }
    }
  } = map!;

  const [
    { cartographyLevelOptions, countriesOptionsSelected, levelOptionSelected, loadingCartographyLevels },
    setState
  ] = useState<State>({
    cartographyLevelOptions: [],
    countriesOptionsSelected: countries,
    levelOptionSelected: level,
    loadingCartographyLevels: false
  });

  const [data] = useCartography({
    config: {
      countries: reportingCountries,
      countryToken: {},
      levelToken: {}
    },
    filters: {
      country: reportingCountries,
      items: {
        level: generalCartographyLevel,
        levelIds: generalCartographyLevelIds
      },
      level: cartography.level
    }
  });

  const { countries: cartographyCountries } = data;

  const updateMapSettingsCartography = useCallback(
    (countriesOptions, levelOption) => {
      setMapCartographySettings({
        id: mapId,
        value: {
          ...cartography,
          countries: countriesOptions,
          level: levelOption,
          status: 'reload'
        }
      });
      dispatch(
        reportingSetCartographyMapConfig({
          countries: countriesOptions,
          level: levelOption
        })
      );
    },
    [setMapCartographySettings]
  );

  const onCountryChange = (value: { id: string }[]) => {
    setState(prevState => ({
      ...prevState,
      countriesOptionsSelected: value.map(i => i.id),
      levelOptionSelected: value.length > 1 ? COUNTRY_LEVEL_CODE : ''
    }));
  };

  const onLevelChange = (value: Record<string, any>) => {
    if (reportingCountries.length === 1) {
      updateMapSettingsCartography(countriesOptionsSelected, value.id);
    }
    setState(prevState => ({
      ...prevState,
      levelOptionSelected: value.id
    }));
  };

  const onApplyFilter = () => {
    updateMapSettingsCartography(countriesOptionsSelected, levelOptionSelected);
  };

  const onResetFilter = () => {
    resetMapCartographySettings({
      id: mapId
    });
    setState(prevState => ({
      ...prevState,
      countryOptionSelected: '',
      levelOptionSelected: ''
    }));
    resetMapCartographyFilter({ id: mapId });
    dispatch(reportingAdvancedFilterResetCartography('maps', mapId));
  };

  const countriesValue = cartographyCountries.filter(c => countriesOptionsSelected.includes(c.id));

  const levelValue = useMemo(() => {
    if (cartographyLevelOptions.length && levelOptionSelected) {
      const levelId = levelOptionSelected;
      return cartographyLevelOptions.filter(l => l.id === levelId);
    }
    return [];
  }, [cartographyLevelOptions, levelOptionSelected]);

  const fetchCartographyLevelSelectorOptions = async (requestToken: Record<string, any>) => {
    try {
      setState(prevState => ({
        ...prevState,
        loadingCartographyLevels: true
      }));
      const countriesToGetCartography =
        reportingCountries.length > 1 ? countriesOptionsSelected.map(c => c.toLowerCase()) : reportingCountries;
      if (countriesToGetCartography.length > 1) {
        setState(prevState => ({
          ...prevState,
          cartographyLevelOptions: [
            {
              id: COUNTRY_LEVEL_CODE,
              label: formatMessage({
                defaultMessage: 'Country',
                id: 'reporting.map.countryLabel'
              })
            }
          ],
          loadingCartographyLevels: false
        }));
        return;
      }
      const countriesInfo: Record<string, any>[] = await getCartographyDictionary(
        countriesToGetCartography,
        requestToken
      );
      const newOptions = countriesInfo[0].tagsConfig
        .filter((tag: Record<string, any>) => tag.reportingMapEventsEnable)
        .map((option: Record<string, any>) => ({
          id: option.id,
          label: option.name
        }));
      setState(prevState => ({
        ...prevState,
        cartographyLevelOptions: newOptions,
        loadingCartographyLevels: false
      }));
    } catch (e: any) {
      // biome-ignore lint/suspicious/noConsole: off
      console.info(e.message);
      setState(prevState => ({
        ...prevState,
        loadingCartographyLevels: false
      }));
    }
  };

  const cartographyLevelToken: Record<string, any> = {};
  useEffect(() => {
    fetchCartographyLevelSelectorOptions(cartographyLevelToken);
    return () => {
      cartographyLevelToken.cancel?.();
    };
  }, [countriesOptionsSelected]);

  useEffect(() => {
    setState(prevState => ({
      ...prevState,
      countryOptionSelected: reportingCountries.length === 1 ? reportingCountries[0]?.toUpperCase() : ''
    }));
  }, [reportingCountries]);

  return (
    <CartographyAdvanceSettingsStyle>
      {campaignCountries.length > 1 && (
        <Selector
          className="innerControls__cartographyCountrySelector"
          id="cartographyCountrySelector"
          label={formatMessage({
            defaultMessage: 'Country',
            id: 'reporting.map.innerControls.mapTypeSwitch.selectors.country.label'
          })}
          onChange={onCountryChange}
          options={cartographyCountries as SelectorOptionPropsInterface[]}
          placeholder={formatMessage({
            defaultMessage: 'Select countries...',
            id: 'reporting.map.innerControls.mapTypeSwitch.selectors.country.placeholder'
          })}
          tiny
          type="multiple"
          value={countriesValue as SelectorOptionPropsInterface[]}
        />
      )}
      <Selector
        className="innerControls__cartographyLevelSelector"
        id="cartographyLevelSelector"
        isDisabled={
          loadingCartographyLevels || (reportingCountries.length > 1 && countriesOptionsSelected.length === 0)
        }
        label={formatMessage({
          defaultMessage: 'View map by',
          id: 'reporting.map.innerControls.mapTypeSwitch.selectors.level.label'
        })}
        onChange={onLevelChange}
        options={cartographyLevelOptions as SelectorOptionPropsInterface[]}
        tiny
        type="simple"
        value={levelValue as SelectorOptionPropsInterface[]}
      />
      {reportingCountries.length > 1 && (
        <ButtonsContainer>
          <ButtonUI
            className="innerControls__trashButton"
            icon="fal fa-trash"
            iconPosition="left"
            onClick={onResetFilter}
          />
          <ButtonUI
            className="innerControls__applyButton"
            disabled={!levelValue.length}
            onClick={onApplyFilter}
            text={formatMessage({
              defaultMessage: 'Apply filter',
              id: 'reporting.map.innerControls.mapTypeSwitch.buttons.apply'
            })}
            type="secondary"
          />
        </ButtonsContainer>
      )}
    </CartographyAdvanceSettingsStyle>
  );
};
